import { Route, Sparkles } from 'lucide-react'
import { useMemo, useState } from 'react'
import StudentBackButton from '../components/StudentBackButton.jsx'
import { useLanguage } from '../contexts/LanguageContext.jsx'

const copy = {
  zh: {
    title: '专业成长路线生成器',
    subtitle: '选择年级、专业方向、目标和当前基础，先用本地模板生成一份可执行路线。',
    grade: ['大一', '大二', '大三', '大四', '研究生'],
    major: ['计算机', '经管', '设计', '机械', '电气', '外语', '其他'],
    goal: ['找实习', '考研', '保研', '比赛', '提升技能', '做项目'],
    level: ['零基础', '学过一点', '有课程项目', '有实习/比赛经历'],
    labels: ['当前年级', '专业方向', '目标', '当前基础'],
    button: '生成成长路线',
    sections: ['当前阶段重点', '需要补的技能', '推荐做的项目/作品', '推荐参加的比赛/证书', '30 天行动计划', '简历包装建议'],
  },
  en: {
    title: 'Major Growth Route Generator',
    subtitle: 'Pick your grade, major, goal, and foundation to generate an actionable local plan.',
    grade: ['Freshman', 'Sophomore', 'Junior', 'Senior', 'Graduate'],
    major: ['Computer Science', 'Business', 'Design', 'Mechanical', 'Electrical', 'Languages', 'Other'],
    goal: ['Internship', 'Postgraduate exam', 'Graduate recommendation', 'Competition', 'Skill growth', 'Project building'],
    level: ['No foundation', 'Basic exposure', 'Course project', 'Internship/competition experience'],
    labels: ['Grade', 'Major', 'Goal', 'Foundation'],
    button: 'Generate growth route',
    sections: ['Current focus', 'Skills to improve', 'Recommended projects/portfolio', 'Competitions/certificates', '30-day action plan', 'Resume packaging advice'],
  },
}

function buildPlan(values, language) {
  const isEn = language === 'en'
  if (isEn) {
    return [
      `Focus on ${values.goal.toLowerCase()} while keeping weekly output visible. Your current foundation is: ${values.level}.`,
      `Build core skills for ${values.major}: research, tool fluency, documentation, and one measurable hard skill.`,
      `Create one portfolio piece: a small product, analysis report, visual case, hardware demo, or bilingual research note.`,
      'Choose one suitable competition or certificate, then prepare proof materials instead of only collecting information.',
      'Week 1: set target and collect examples. Week 2: finish the first draft. Week 3: get feedback. Week 4: polish and publish.',
      'Write achievements with context, action, tool/skill, and result. Replace vague effort with visible output.',
    ]
  }
  return [
    `当前重点是围绕“${values.goal}”建立可展示成果，同时补齐“${values.level}”阶段最缺的基础。`,
    `${values.major}方向建议优先补：资料检索、工具使用、表达呈现、一个可量化的核心技能。`,
    '推荐做一个可展示作品：小产品、数据分析报告、视觉案例、硬件 Demo 或双语调研笔记。',
    '选择 1 个匹配目标的比赛或证书，不要只收藏信息，要同步准备作品、报告和证明材料。',
    '第 1 周定目标和找样例；第 2 周完成初稿；第 3 周找反馈；第 4 周打磨发布并整理复盘。',
    '简历里用“背景 + 行动 + 技能/工具 + 结果”表达，把空泛努力改成可验证成果。',
  ]
}

function StudentGrowthPlanPage() {
  const { language } = useLanguage()
  const t = copy[language] || copy.zh
  const [values, setValues] = useState({
    grade: t.grade[0],
    major: t.major[0],
    goal: t.goal[0],
    level: t.level[0],
  })
  const [generated, setGenerated] = useState(false)
  const groups = useMemo(() => [
    ['grade', t.labels[0], t.grade],
    ['major', t.labels[1], t.major],
    ['goal', t.labels[2], t.goal],
    ['level', t.labels[3], t.level],
  ], [t])
  const plan = buildPlan(values, language)

  return (
    <div className="student-page page-container">
      <StudentBackButton />
      <section className="student-hero">
        <span>{language === 'en' ? 'Student Zone' : '学生专区'}</span>
        <h1>{t.title}</h1>
        <p>{t.subtitle}</p>
      </section>
      <section className="student-form-panel">
        {groups.map(([key, label, options]) => (
          <label key={key}>
            <span>{label}</span>
            <select value={values[key]} onChange={(event) => setValues((current) => ({ ...current, [key]: event.target.value }))}>
              {options.map((option) => <option key={option}>{option}</option>)}
            </select>
          </label>
        ))}
        <button className="primary-button" type="button" onClick={() => setGenerated(true)}>
          <Sparkles size={16} />
          {t.button}
        </button>
      </section>
      {generated && (
        <section className="student-result-panel">
          <Route size={24} />
          {t.sections.map((section, index) => (
            <article key={section}>
              <h2>{index + 1}. {section}</h2>
              <p>{plan[index]}</p>
            </article>
          ))}
        </section>
      )}
    </div>
  )
}

export default StudentGrowthPlanPage
